
import { defineComponent } from 'vue';

import { SocialThreadType } from '@/models/Social/social-dialog';
import { SocialMention } from '@/models/Social/social-mention';
import { SocialProfile } from '@/models/Social/social-profile';
import { SocialTabProducts } from '@/models/Social/Profile/social-tab-products';
import { SocialTabQuestions } from '@/models/Social/Profile/social-tab-questions';
import { BlockProduct } from '@/models/Blocks/Itens/block-product';

import baseUrl from "@/api/base-url";
import httpRequest from "@/Utilities/httpRequest";
import utilities from '@/Utilities/common';
import operatingSystem from "@/Utilities/operating-system";
import browser from "@/Utilities/browser";

export default defineComponent({
    props: {
        mention: {
            type: SocialMention,
            default: undefined
        },
        contentUrl: {
            type: String,
            default: baseUrl.getUrl('socialNetwork/content')
        },
        saveCommentUrl: {
            type: String,
            default: baseUrl.getUrl('socialNetwork/save-comment')
        },
        kStrings:{
            type: Object,
            default: () => {
                return {};
            }
        },
        isGuest: {
            type: Boolean,
            default: true
        },
        isAppStore:{
            type: Boolean,
            default: false
        },
        context:{
            type: String,
            default: ''
        },
        clubMode: {
            type: Boolean,
            default: false
        },
        preloadedPosts: {
            type: [Object, null],
            default: null
        }
    },
    data(){
        let products: SocialTabProducts|undefined;
        let questions: SocialTabQuestions|undefined;
        return {
            showTabs: false,
            showPost: true,
            closeTabs: true,
            tabSelected: '',
            products,
            questions,
            currentSwipeDirection: ''
        }
    },
    computed:{
        socialMention(): SocialMention | undefined{
            return (this.mention instanceof SocialMention) ? this.mention : undefined;
        },
        socialProfile(): SocialProfile | undefined{
            return this.socialMention?.profile ?? undefined;
        },
        isQuestionsTab(): boolean{
            return this.tabSelected == 'questions';
        },
        isProductsTab(): boolean{
            return this.tabSelected == 'products';
        },
        questionsTabTitle(): string{
            return this.kStrings?.questionsTabTitle ?? '';
        },
        productsTabTitle(): string{
            return this.kStrings?.productsTabTitle ?? '';
        },
        productsTab():SocialTabProducts|undefined{
            let postId = this.mention?.post.id ?? null;
            if(!this.products && postId){
                this.products = new SocialTabProducts(this.productsTabTitle,{
                    type: 'products',
                    postId: postId,
                    profile: this.mention?.profile.id ?? null
                },this.contentUrl);
                this.products?.nextPage();
            }
            return this.products instanceof SocialTabProducts ? this.products : undefined;
        },
        questionsTab():SocialTabQuestions|undefined{
            let postId = this.mention?.post.id ?? null;
            if(!this.questions){
                this.questions = new SocialTabQuestions(this.questionsTabTitle,{
                    type: 'dialog',
                    postId: postId,
                    name: this.mention.profile.name ?? ''
                },this.contentUrl);

                if(!this.questions.hasContent()){
                    this.questions.nextPage();
                }
            }
            return this.questions instanceof SocialTabQuestions ? this.questions : undefined;
        },
        isAbleToDoNewComment(): boolean {
            let dialogs = this.questions?.dialogs ?? [];
            for (const e of dialogs) {
                if(e?.thread?.type == SocialThreadType.PendingComment){
                    return false;
                    
                }
            };
            return true;
        },
        productsList(): Array<BlockProduct>|undefined{
            if(this.productsTab instanceof SocialTabProducts){
                return this.productsTab.getProducts();
            }
            return undefined;
        },
        productsMubData(){
            return !this.mention || !this.mention.post ? null : this.mention.post.productsMub;
        },
        isSafariIos() {
            return operatingSystem.isIOS() && browser.getBrowser() == 'safari'
        },
        postNavigation(): Object | null  {
            if(!this.clubMode || (!this.mention.post.previousPostId && !this.mention.post.nextPostId)) {
                return null;
            }

            return {
                previousPostId: this.mention.post.previousPostId,
                nextPostId: this.mention.post.nextPostId,
            }
        },
        swipeTransitionName() {
            if(this.currentSwipeDirection == 'top') {
                return 'modal-slide-vertical'
            } else if(this.currentSwipeDirection == 'bottom') {
                return 'modal-slide-vertical-inverse'
            } else if(this.currentSwipeDirection == 'left') {
                if(this.isSafariIos) {
                    return 'safari-history-horizontal';
                }
                return 'modal-slide-horizontal';
            }

            return 'fade';
        }
    },
    methods: {
        image(product: BlockProduct): string|undefined{
            return product.images?.main ?? undefined;
        },
        priceCrossed(product: BlockProduct): string|undefined{
            return product.price?.crossed ?? undefined;
        },
        questionsReload(){
            if(this.questionsTab){
                this.questionsTab.reload();
            }
        },
        openTabs(){
            this.showTabs = true;
            this.closeTabs = false;
            if(!this.clubMode) {
                setTimeout(
                    () => { this.showPost = false;},
                    500
                );
            }
        },
        hideTabs(){
            this.showPost = true;
            this.closeTabs = true;
            setTimeout(() => this.showTabs = false, 500);
        },
        action(type: string){
            this.openTabs();
            this.tabSelected = type;
        },
        touchCommand(direction) { // for mobile
            //return null; // disabled for now
            this.currentSwipeDirection = direction;
            if(this.preloadedPosts) { // has preloaded previous && next post data
                if(direction == 'top') {
                    // go previous
                    this.$emit('reload-data', {profile: this.preloadedPosts?.previous?.profile, post: this.preloadedPosts?.previous?.post});
                } else if(direction == 'bottom') {
                    // go next
                    this.$emit('reload-data', {profile: this.preloadedPosts?.next?.profile, post: this.preloadedPosts?.next?.post});
                }
            }
           /* else if(this.postNavigation) {
                let url = '';
                if(direction == 'top') {
                    // go previous
                    url = this.postNavigation.previousPostId;
                } else if(direction == 'bottom' || direction == 'left') {
                    // go next
                    url = this.postNavigation.nextPostId;
                }

                if(url) {
                    this.loadPost(url);
                }
            }*/

            if(direction == 'right') { // maybe does not work on safari
                // go profile
                utilities.goToUrl(baseUrl.getUrl(this.socialProfile.url));
            }
        },
        async loadPost(postId = null) {
            if(postId) {
                let requestUrl = baseUrl.getUrl('socialNetwork/post?oti=PDP_SOCIAL');
                //let urlParams = new URLSearchParams(url);
                let params = {
                    profileid: this.mention.profile.id,
                    postId: postId,
                    profileName: this.mention.profile.nameId
                }
                const {data} = await httpRequest.get(requestUrl, params);
                // todo try catch, events, etc
                this.$emit('reload-data', {profile: data.profile, post: data.post});
            }
        }
    }
});
